/* Mojito — native PDP enhancements (in-place replacement for Globo + Rubik + VDK).
   - Labels:   rewrite each option legend to "Color: Mauve" / "Size: L".
   - Gallery:  CUSTOM per-variant gallery. Reuses the theme's server-rendered slide
               nodes (so SEO crawl + the eager/preloaded LCP image are preserved, with
               zero duplicate DOM and no double-download), but takes them OUT of the
               theme's Flickity slider into our own responsive track:
                 • >=1024px: a 2-column grid (matches the theme's desktop look)
                 • <1024px : a native scroll-snap carousel + a thumbnail rail
               No Flickity -> hiding a slide can never collapse the viewport (the old
               "main image disappears while resizing" bug). Shows ONLY the selected
               color's images, with a GRACEFUL show-all fallback when a product's
               filenames can't be mapped to colors confidently (never the wrong color).
   - Description: turn each <h4> section into a collapsible accordion (first open).
   Color swatches + size boxes are the theme's NATIVE selectors, restyled in the CSS.

   The theme re-renders the product form on every variant change via the Section
   Rendering API (which destroys listeners bound to the inputs) but it does NOT
   rebuild the gallery slides — they are static for the life of the page. So we use
   EVENT DELEGATION on document + a guarded MutationObserver, always re-querying the
   live DOM, and we build the gallery once. */
(function () {
  'use strict';

  var LARGE = 1024; // theme.sizes.large — grid above, carousel below

  // The theme swaps <variant-selects> on variant change via an ASYNC viewTransition,
  // while Price/Inventory swap synchronously and fire our MutationObserver first — so
  // a mid-transition refresh would re-read a STALE :checked color and flash the gallery
  // to the wrong color. We therefore remember the color the user actually picked (from
  // the change event) and the last confidently-mapped token plan, and always resolve to
  // those instead of trusting the transient DOM.
  var lastColorVal = null; // authoritative selected color value (survives the swap)
  var lastPlan = null;     // last confident {strat, tokenByVal} (avoids transient show-all)

  /* =====================================================================
     FILENAME -> COLOR TOKEN (multi-strategy, so we can auto-recover the
     common ambiguous filename styles instead of falling back to show-all).
     We never trust a single heuristic: we pick the FIRST strategy that gives
     every color a DISTINCT token; if none do, we show all (safe fallback).
     ===================================================================== */
  function cleanName(src) {
    if (!src) return '';
    var b = src.split('?')[0].split('#')[0].split('/').pop();
    b = b.replace(/\.[a-z0-9]+$/i, '');   // drop extension
    // drop Shopify's collision-avoidance UUID suffix (…_xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
    // appended when a re-uploaded/duplicated image's filename already exists. Without this,
    // sDashLast() reads the UUID tail as the "color" → every image looks like its own color
    // → the per-color filter keeps almost everything (oversized grid / mobile gallery gap).
    b = b.replace(/[_-][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, '');
    b = b.replace(/[_-]\d{1,3}$/, '');    // drop trailing image index (_1 / -2)
    return b;
  }
  // last "-" segment, color portion before any "_"  (SKU-COLOR -> COLOR; also M-5235-KHAKI -> KHAKI)
  function sDashLast(b) { var p = b.split('-'); return (p[p.length - 1] || '').split('_')[0].toUpperCase(); }
  // last "_" segment (MGC-A006_NATURAL -> NATURAL); empty when no underscore
  function sUnderscoreLast(b) { if (b.indexOf('_') < 0) return ''; var p = b.split('_'); return (p[p.length - 1] || '').toUpperCase(); }
  // last TWO "-" segments (two-tone: SKU-WHITE-NAVY -> WHITE-NAVY)
  function sDashTwo(b) { var p = b.split('-'); if (p.length < 2) return ''; return p.slice(-2).join('-').split('_')[0].toUpperCase(); }
  var STRATS = [sDashLast, sUnderscoreLast, sDashTwo];
  function tokenWith(strat, src) { return strat(cleanName(src)); }

  function scopeEl() { return document.querySelector('product-info') || document.body; }

  function colorInputs(scope) {
    return [].slice.call(scope.querySelectorAll('input[type="radio"][name]'))
      .filter(function (i) { return /^options\[/i.test(i.name) && /colou?r/i.test(i.name); });
  }
  function swatchImg(input) {
    var h = input.closest('.radio__button') || input.parentNode;
    return h && h.querySelector('.option-image img, img');
  }

  /* Decide how to map this product's colors -> tokens.
     Returns { hasColor, showAll, strat, tokenByVal }. */
  function colorPlan(scope) {
    var inputs = colorInputs(scope);
    if (inputs.length < 2) return { hasColor: inputs.length > 0, showAll: true, strat: null, tokenByVal: {} };

    for (var s = 0; s < STRATS.length; s++) {
      var strat = STRATS[s], map = {}, seen = {}, ok = true, distinct = true;
      for (var k = 0; k < inputs.length; k++) {
        var img = swatchImg(inputs[k]);
        var tok = img ? tokenWith(strat, img.getAttribute('src') || img.src) : '';
        if (!tok) { ok = false; break; }       // a color with no usable token -> strategy fails
        if (seen[tok]) distinct = false;        // two colors share a token -> not unique
        seen[tok] = 1;
        map[inputs[k].value] = tok;
      }
      if (ok && distinct) return { hasColor: true, showAll: false, strat: strat, tokenByVal: map };
    }
    // No strategy makes the colors distinct -> graceful fallback: show everything.
    return { hasColor: true, showAll: true, strat: null, tokenByVal: {} };
  }

  /* =====================================================================
     OPTION LABELS ("Color: Mauve" / "Size: L")
     ===================================================================== */
  function updateLabels() {
    var scope = scopeEl();
    [].slice.call(scope.querySelectorAll('.product__selectors .selector-wrapper')).forEach(function (w) {
      var lbl = w.querySelector('.radio__legend__label');
      if (!lbl) return;
      var base = lbl.getAttribute('data-mojito-base');
      if (base === null) {
        base = (lbl.textContent || '').replace(/\s*:\s*$/, '').trim();
        lbl.setAttribute('data-mojito-base', base);
      }
      var checked = w.querySelector('.radio__input:checked, .swatch__input:checked');
      var val = checked ? (checked.getAttribute('value') || '') : '';
      lbl.textContent = base;
      if (val) {
        lbl.appendChild(document.createTextNode(': '));
        var span = document.createElement('span');
        span.className = 'mojito-sel-val';
        span.textContent = val;
        lbl.appendChild(span);
      }
    });
  }

  /* =====================================================================
     REVIEW COUNT next to the Judge.me stars
     ===================================================================== */
  function updateReviewCount() {
    var scope = scopeEl();
    var badge = scope.querySelector('.jdgm-prev-badge');
    if (!badge) return;
    var n = parseInt(badge.getAttribute('data-number-of-reviews'), 10);
    if (isNaN(n)) {
      var txt = badge.querySelector('.jdgm-prev-badge__text');
      var src = (txt && txt.textContent) || badge.getAttribute('aria-label') || '';
      var m = src.match(/(\d[\d,]*)/);
      n = m ? parseInt(m[1].replace(/,/g, ''), 10) : NaN;
    }
    var existing = badge.querySelector('.mojito-review-count');
    if (isNaN(n) || n <= 0) { if (existing && existing.parentNode) existing.parentNode.removeChild(existing); return; }
    if (!existing) {
      existing = document.createElement('span');
      existing.className = 'mojito-review-count';
      var stars = badge.querySelector('.jdgm-prev-badge__stars');
      if (stars) stars.insertAdjacentElement('afterend', existing);
      else badge.appendChild(existing);
    }
    var label = '(' + n + ')';
    if (existing.textContent !== label) existing.textContent = label;
  }

  /* =====================================================================
     CUSTOM GALLERY (replaces Rubik AND the theme's Flickity slideshow)
     ===================================================================== */

  // Stop the theme from ever running Flickity on the gallery (slider + thumb nav)
  // and from swapping/scrolling media itself — we own the gallery now.
  function neutralizeTheme(pi) {
    if (!pi || pi.__mojitoNeutralized) return;
    try { if (pi.flkty) pi.flkty.destroy(); } catch (e) {}
    try { if (pi.flktyNav) pi.flktyNav.destroy(); } catch (e) {}
    pi.flkty = null; pi.flktyNav = null;
    pi.initProductSlider = function () {}; // shadow prototype method on this instance
    pi.updateMedia = function () {};        // no theme media-switch / scroll-to-featured
    pi.__mojitoNeutralized = true;
  }

  function clearFlickityInline(el) {
    // After destroy Flickity usually restores nodes, but be defensive.
    var s = el.style;
    s.position = ''; s.left = ''; s.top = ''; s.right = ''; s.transform = ''; s.width = '';
  }

  function scrollStageTo(stage, slide, smooth) {
    var left = Math.max(0, slide.offsetLeft); // slide.offsetParent === stage (position:relative)
    try { stage.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' }); }
    catch (e) { stage.scrollLeft = left; }
  }
  // The visible slide nearest the stage's horizontal centre (the one snapped into view).
  function stageActiveSlide(stage) {
    var mid = stage.scrollLeft + stage.clientWidth / 2, best = null, bestD = Infinity;
    [].slice.call(stage.querySelectorAll('.mojito-g-slide')).forEach(function (sl) {
      if (sl.classList.contains('mojito-hide')) return;
      var c = sl.offsetLeft + sl.offsetWidth / 2, d = Math.abs(c - mid);
      if (d < bestD) { bestD = d; best = sl; }
    });
    return best;
  }
  // CAROUSEL ONLY: match the stage's height to the slide in view. The <1024px track is a
  // flex row, so its height defaults to the TALLEST slide — a single off-ratio media (a size
  // chart, flat-lay, banner) then stretches the row and leaves an empty gap above the thumb
  // rail under every shorter photo. (Flickity used to equalize this; our CSS carousel doesn't.)
  // >=LARGE is a CSS grid that sizes to content, so we clear any inline height there.
  function syncStageHeight(stage, slide) {
    if (!stage) return;
    if (window.innerWidth >= LARGE) { stage.style.height = ''; return; }
    var s = slide || stageActiveSlide(stage);
    if (!s) return;
    var img = s.querySelector('img');
    var h = (img || s).getBoundingClientRect().height;
    if (h) stage.style.height = Math.round(h) + 'px';
  }
  function thumbFor(thumbs, id) { return thumbs.querySelector('.mojito-g-thumb[data-target-id="' + id + '"]'); }

  // 'thumbnails' paints the rail image; 'dots' and 'none' do not. Set by
  // sections/mojito-pdp-gallery.liquid. Default to the image-painting behaviour so a
  // page without that section (or with the flag missing) keeps the old markup.
  function railShowsImages() {
    var nav = window.MOJITO_GALLERY_NAV;
    return nav !== 'dots' && nav !== 'none';
  }
  function setActiveThumb(thumbs, btn) {
    [].slice.call(thumbs.children).forEach(function (b) { b.classList.toggle('is-active', b === btn); });
    if (btn && btn.scrollIntoView) { try { btn.scrollIntoView({ inline: 'nearest', block: 'nearest' }); } catch (e) {} }
  }
  function syncActiveByScroll(stage, thumbs) {
    var best = stageActiveSlide(stage);
    if (best) { var t = thumbFor(thumbs, best.getAttribute('data-id')); if (t) setActiveThumb(thumbs, t); }
    syncStageHeight(stage, best); // keep the stage as tall as the slide in view (no gap)
  }

  // Build the custom gallery once; returns { gallery, stage, thumbs } or null.
  function buildGallery(scope) {
    var gallery = scope.querySelector('[data-product-single-media-group], .product-gallery');
    if (!gallery) return null;

    var existing = gallery.querySelector('.mojito-g-stage');
    if (existing) return { gallery: gallery, stage: existing, thumbs: gallery.querySelector('.mojito-g-thumbs') };

    neutralizeTheme(document.querySelector('product-info'));
    lastColorVal = null; lastPlan = null; // fresh gallery (initial load / product swap) -> reset caches

    var slider = gallery.querySelector('[data-product-single-media-slider]');
    var origThumbs = gallery.querySelector('[data-product-single-media-thumbs]');
    var slides = [].slice.call(gallery.querySelectorAll('[data-product-single-media-wrapper]'));
    if (slides.length < 1) return null;

    var stage = document.createElement('div');
    stage.className = 'mojito-g-stage';
    var thumbs = document.createElement('div');
    thumbs.className = 'mojito-g-thumbs';

    slides.forEach(function (sl, idx) {
      clearFlickityInline(sl);
      sl.classList.add('mojito-g-slide');
      stage.appendChild(sl); // reparent the actual node (no copy -> no extra download)

      var mediaId = sl.getAttribute('data-id') || String(idx);
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mojito-g-thumb';
      btn.setAttribute('data-target-id', mediaId);
      btn.setAttribute('aria-label', 'Show image ' + (idx + 1));

      // In 'dots'/'none' nav the rail is drawn as CSS circles and the <img> inside is
      // display:none (mojito-pdp-gallery.liquid). An <img> with a src still downloads
      // when it is display:none, so giving it one costs a request per media and paints
      // nothing. Only populate the image when the rail actually shows thumbnails.
      if (railShowsImages()) {
        var img = document.createElement('img');
        var tImg = origThumbs && origThumbs.querySelector('.product-single__thumbnail[data-id="' + mediaId + '"] img');
        if (tImg) { // reuse the theme's small thumbnail image (cheap)
          img.src = tImg.getAttribute('src') || tImg.src;
          if (tImg.getAttribute('srcset')) img.srcset = tImg.getAttribute('srcset');
          img.sizes = '64px';
        } else {
          var sImg = sl.querySelector('img');
          if (sImg) img.src = sImg.getAttribute('src') || sImg.src;
        }
        img.alt = '';
        img.loading = 'lazy';
        img.setAttribute('aria-hidden', 'true');
        btn.appendChild(img);
      }
      thumbs.appendChild(btn);
    });

    // Insert our gallery; retire the theme's slider + native thumb strip.
    if (slider) { slider.classList.add('mojito-g-orig'); slider.parentNode.insertBefore(stage, slider); }
    else gallery.insertBefore(stage, gallery.firstChild);
    if (origThumbs) origThumbs.classList.add('mojito-g-orig');
    gallery.insertBefore(thumbs, stage.nextSibling);
    gallery.classList.add('mojito-g');

    thumbs.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('.mojito-g-thumb') : null;
      if (!b) return;
      var id = b.getAttribute('data-target-id');
      var target = stage.querySelector('.mojito-g-slide[data-id="' + id + '"]');
      if (target) scrollStageTo(stage, target, true);
      setActiveThumb(thumbs, b);
    });

    var raf = null;
    stage.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; syncActiveByScroll(stage, thumbs); });
    }, { passive: true });

    // Re-fit the stage height across breakpoint / orientation changes (and clear it on desktop).
    var rzRaf = null;
    window.addEventListener('resize', function () {
      if (rzRaf) return;
      rzRaf = requestAnimationFrame(function () { rzRaf = null; syncStageHeight(stage); });
    }, { passive: true });

    // Slide images are lazy: re-fit once each finishes loading so the height tracks the real image.
    [].slice.call(stage.querySelectorAll('.mojito-g-slide img')).forEach(function (im) {
      if (im.complete) return;
      im.addEventListener('load', function () { syncStageHeight(stage); }, { once: true });
    });

    return { gallery: gallery, stage: stage, thumbs: thumbs };
  }

  // Filter to the selected color's images (with graceful fallback) + set the lead image.
  function applyGallery() {
    var scope = scopeEl();
    var built = buildGallery(scope);
    if (!built) return;
    var stage = built.stage, thumbs = built.thumbs;
    var slides = [].slice.call(stage.querySelectorAll('.mojito-g-slide'));
    if (!slides.length) return;

    var plan = colorPlan(scope);
    if (plan.hasColor && !plan.showAll && plan.strat) lastPlan = plan; // remember a confident mapping
    var usePlan = (plan.hasColor && !plan.showAll && plan.strat) ? plan
                : (lastPlan && lastPlan.strat ? lastPlan : plan); // ignore a transient show-all mid-swap

    var hideFlag = [];
    if (!usePlan.hasColor || usePlan.showAll || !usePlan.strat) {
      slides.forEach(function () { hideFlag.push(false); }); // show all (no confident color mapping)
    } else {
      var inputs = colorInputs(scope);
      // Selected color = the one the user actually picked (survives the theme's async
      // <variant-selects> swap), else the live :checked, else the first. Never a stale value.
      var selVal = null;
      if (lastColorVal != null) {
        for (var a = 0; a < inputs.length; a++) { if (inputs[a].value === lastColorVal) { selVal = lastColorVal; break; } }
      }
      if (selVal == null) {
        var ck = inputs.filter(function (i) { return i.checked; })[0] || inputs[0];
        selVal = ck ? ck.value : null;
      }
      var token = usePlan.tokenByVal[selVal];
      var colorTokens = {};
      for (var v in usePlan.tokenByVal) colorTokens[usePlan.tokenByVal[v]] = 1;

      var keep = slides.map(function (sl) {
        var img = sl.querySelector('img');
        var mt = img ? tokenWith(usePlan.strat, img.getAttribute('src') || img.src) : '';
        var common = !colorTokens[mt];       // not any color's token -> shared (model/lifestyle) shot
        return common || mt === token;
      });
      var anyKept = keep.indexOf(true) !== -1; // never blank the gallery
      keep.forEach(function (k) { hideFlag.push(anyKept ? !k : false); });
    }

    slides.forEach(function (sl, i) {
      sl.classList.toggle('mojito-hide', hideFlag[i]);
      var t = thumbFor(thumbs, sl.getAttribute('data-id'));
      if (t) t.classList.toggle('mojito-hide', hideFlag[i]);
    });

    // Lead = first visible slide (Shopify orders each color's featured first in its group).
    var lead = stage.querySelector('.mojito-g-slide:not(.mojito-hide)');
    if (lead) {
      if (window.innerWidth < LARGE) stage.scrollLeft = Math.max(0, lead.offsetLeft);
      var lt = thumbFor(thumbs, lead.getAttribute('data-id'));
      if (lt) setActiveThumb(thumbs, lt);
    }
    syncStageHeight(stage, lead); // size the carousel to the lead image so no gap opens
  }

  /* =====================================================================
     H4 DESCRIPTION ACCORDION (replaces VDK)
     ===================================================================== */
  function initAccordion() {
    var scope = scopeEl();
    var sels = ['.product__description', '[class*="main-product-description"]',
                '.product__full-width-blocks', '.product-single__description', '.rte'];
    var desc = null;
    for (var i = 0; i < sels.length && !desc; i++) {
      var els = [].slice.call(scope.querySelectorAll(sels[i]));
      for (var j = 0; j < els.length; j++) { if (els[j].querySelector('h4')) { desc = els[j]; break; } }
    }
    if (!desc || desc.getAttribute('data-mojito-acc')) return;
    desc.setAttribute('data-mojito-acc', '1');
    desc.classList.add('mojito-acc');

    [].slice.call(desc.querySelectorAll('h4')).forEach(function (h4, idx) {
      var content = document.createElement('div');
      content.className = 'mojito-acc-content';
      var sib = h4.nextSibling;
      while (sib && !(sib.nodeType === 1 && sib.tagName === 'H4')) {
        var next = sib.nextSibling; content.appendChild(sib); sib = next;
      }
      var item = document.createElement('div');
      item.className = idx === 0 ? 'mojito-acc-item is-open' : 'mojito-acc-item';
      desc.insertBefore(item, h4);
      h4.classList.add('mojito-acc-header');
      h4.setAttribute('role', 'button'); h4.setAttribute('tabindex', '0');
      item.appendChild(h4); item.appendChild(content);
      function toggle() { item.classList.toggle('is-open'); }
      h4.addEventListener('click', toggle);
      h4.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
    });
  }

  /* =====================================================================
     CONTEXTUAL AVAILABILITY — grey out (color) / diagonal-cross (size) any
     option value that has NO in-stock variant given the OTHER currently
     selected options. The theme's native `value.available` is GLOBAL (a size
     counts as available if it's in stock for ANY color), so it cannot show
     "sold out for THIS color". We compute it from the product's variants and
     toggle `.mojito-oos`, which the CSS styles like `.sold-out`.
     ===================================================================== */
  var productData = null, productPending = false;
  function productHandle() {
    var m = (window.location.pathname || '').match(/\/products\/([^\/?#]+)/i);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function loadProduct() {
    if (productData || productPending) return;
    var h = productHandle(); if (!h) return;
    productPending = true;
    // Locale root matters: /es/ pages carry TRANSLATED option names/values in the DOM
    // (options[Tamaño], BLANCO-CAPUCHINO), and only the locale-prefixed .js endpoint
    // returns matching data — the bare endpoint is always English, which made every
    // value/name lookup below miss and greyed ALL color chips on /es/.
    var root = ((window.Shopify || {}).routes || {}).root || '/';
    fetch(root + 'products/' + h + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (p) { productData = p; productPending = false; refresh(); })
      .catch(function () { productPending = false; });
  }
  function optionNames(p) {
    var opts = p.options || [], out = [];
    for (var i = 0; i < opts.length; i++) out.push(typeof opts[i] === 'string' ? opts[i] : (opts[i] && opts[i].name) || '');
    return out;
  }
  function optionNameOf(input) { var m = input.name.match(/^options\[(.+)\]$/i); return m ? m[1] : input.name; }
  function optionInputs(scope) {
    return [].slice.call(scope.querySelectorAll('input[type="radio"][name]'))
      .filter(function (i) { return /^options\[/i.test(i.name); });
  }
  function applyAvailability() {
    if (!productData) { loadProduct(); return; } // load once, then this re-runs
    var scope = scopeEl();
    var names = optionNames(productData);
    var variants = productData.variants || [];
    if (!names.length || !variants.length) return;
    var inputs = optionInputs(scope);
    if (!inputs.length) return;

    var idxByName = {};
    for (var n = 0; n < names.length; n++) idxByName[names[n].toLowerCase()] = n;

    // selected value per option (color uses the user-picked value so it survives the swap)
    var selected = {};
    inputs.forEach(function (i) { if (i.checked) selected[optionNameOf(i).toLowerCase()] = i.value; });
    if (lastColorVal != null) {
      inputs.forEach(function (i) { if (/colou?r/i.test(i.name)) selected[optionNameOf(i).toLowerCase()] = lastColorVal; });
    }

    // a value is available if SOME in-stock variant has it AND matches every OTHER selection
    function available(optIdx, val) {
      for (var v = 0; v < variants.length; v++) {
        var vr = variants[v];
        if (!vr.available || !vr.options) continue;
        if (String(vr.options[optIdx]) !== String(val)) continue;
        var ok = true;
        for (var p = 0; p < names.length; p++) {
          if (p === optIdx) continue;
          var sel = selected[names[p].toLowerCase()];
          if (sel != null && String(vr.options[p]) !== String(sel)) { ok = false; break; }
        }
        if (ok) return true;
      }
      return false;
    }

    inputs.forEach(function (i) {
      var oi = idxByName[optionNameOf(i).toLowerCase()];
      var btn = i.closest ? i.closest('.radio__button, .swatch__button') : null;
      if (oi == null || !btn) return;
      btn.classList.toggle('mojito-oos', !available(oi, i.value));
    });
  }

  /* =====================================================================
     SWATCH PHOTOS the theme couldn't resolve. The theme reads each color chip's
     photo from `value.variant` (product-option-value.liquid), and Shopify resolves
     that against the OTHER currently selected options — so a color with no variant
     in the selected size renders a placeholder <svg> instead of its photo. Common on
     Big & Tall, where the color range differs per size band. It also starves
     colorPlan() (which needs one <img> per color to map filenames -> tokens), so a
     single placeholder silently disables color filtering for the WHOLE gallery.
     Fill the gaps from the product JSON we already load for availability; the chip's
     own colorway photo is correct regardless of which size is selected.
     ===================================================================== */
  function colorImageByValue() {
    var names = optionNames(productData), ci = -1;
    for (var n = 0; n < names.length; n++) if (/colou?r/i.test(names[n])) ci = n;
    if (ci < 0) return null;
    var map = {}, variants = productData.variants || [];
    for (var v = 0; v < variants.length; v++) {
      var vr = variants[v], src = vr.featured_image && vr.featured_image.src;
      if (!src || !vr.options) continue;
      var key = String(vr.options[ci]);
      if (!map[key]) map[key] = src;            // first image wins, matching the theme
    }
    return map;
  }
  function fillSwatchImages() {
    if (!productData) { loadProduct(); return; } // load once, then this re-runs
    var inputs = colorInputs(scopeEl());
    if (inputs.length < 2) return;
    var map = null;
    inputs.forEach(function (i) {
      var host = i.closest ? i.closest('.radio__button') : null;
      var holder = host && host.querySelector('.option-image');
      if (!holder || holder.querySelector('img')) return; // theme resolved it already
      if (!map) map = colorImageByValue();
      var src = map && map[i.value];
      if (!src) return;
      var url = src.indexOf('//') === 0 ? 'https:' + src : src;
      var img = document.createElement('img');
      img.src = url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=180';
      img.alt = '';                              // the adjacent .option-title names the color
      img.loading = 'lazy';
      img.setAttribute('data-mojito-swatch-fill', '');
      holder.innerHTML = '';                     // drop the placeholder svg
      holder.appendChild(img);
    });
  }

  /* =====================================================================
     SIZE GUIDE (Kiwi Size Chart app) — relocate its "Size Guide" trigger onto
     the Size legend line (right-aligned), mobile + desktop. We only MOVE the
     app's own element (its markup + click handler are left intact) and re-attach
     it after the theme re-renders <variant-selects>. We HOLD a reference so the
     node is never lost when the legend is replaced.
     ===================================================================== */
  var sizeGuideEl = null;
  function sgVisible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || (el.getClientRects && el.getClientRects().length)));
  }
  function findSizeGuide() {
    // the trigger reads "Size Guide"/"Size Chart" (allow a leading/trailing icon or arrow);
    // it may be a <div>/<span>, not just a/button. Pick the DEEPEST visible match, then
    // climb to its clickable host so moving it keeps the app's click working.
    var rx = /^[^a-z0-9]*(size\s*guide|size\s*chart)[^a-z0-9]*$/i;
    var roots = [document.querySelector('product-info'), document];
    for (var r = 0; r < roots.length; r++) {
      var root = roots[r]; if (!root) continue;
      var nodes = root.querySelectorAll('a, button, [role="button"], span, div, p, label, strong, em');
      var best = null, bestDepth = -1;
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el.classList && el.classList.contains('mojito-sizeguide-inline')) continue;
        var t = (el.textContent || '').replace(/[\s ]+/g, ' ').trim();
        if (!rx.test(t) || !sgVisible(el)) continue;
        var depth = 0, p = el; while (p) { depth++; p = p.parentElement; }
        if (depth > bestDepth) { bestDepth = depth; best = el; }
      }
      if (!best) continue;
      // Climb UP to the OUTERMOST wrapper still containing ONLY "Size Guide" — the whole Kiwi
      // trigger unit — so its click listener travels with the move. WHY: Kiwi binds the click
      // via addEventListener on a plain <div> (no onclick attr / no role), so the old approach
      // (drill to the deepest text node, .closest() back to a/button) returned a bare span and
      // ORPHANED it from the handler-bearing wrapper → dead plain text. Stop before the parent
      // gains other content (e.g. the hidden chart modal) or hits a theme structural element.
      var STOP = 'legend, fieldset, form, product-form, variant-selects, .product__form, .product__selectors, .selector-wrapper';
      var host = best;
      while (host.parentElement && host.parentElement !== root &&
             !(host.parentElement.matches && host.parentElement.matches(STOP))) {
        var pt = (host.parentElement.textContent || '').replace(/\s+/g, ' ').trim();
        if (!rx.test(pt)) break;        // parent holds other content → stop here
        host = host.parentElement;
      }
      return host;
    }
    return null;
  }
  function sizeLegend(scope) {
    var w = scope.querySelector('.selector-wrapper--boxes:not(.selector-wrapper--swatches)');
    if (w) { var l = w.querySelector('.radio__legend'); if (l) return l; }
    var legends = scope.querySelectorAll('.product__selectors .radio__legend');
    for (var i = 0; i < legends.length; i++) {
      var lbl = legends[i].querySelector('.radio__legend__label');
      var base = lbl ? (lbl.getAttribute('data-mojito-base') || lbl.textContent || '') : '';
      if (/size/i.test(base)) return legends[i];
    }
    return null;
  }
  function placeSizeGuide() {
    var scope = scopeEl();
    if (!sizeGuideEl) {                       // find once, then keep the reference forever
      sizeGuideEl = findSizeGuide();
      if (sizeGuideEl) sizeGuideEl.classList.add('mojito-sizeguide-inline');
    }
    if (!sizeGuideEl) return;
    var legend = sizeLegend(scope);
    if (legend && sizeGuideEl.parentNode !== legend) legend.appendChild(sizeGuideEl); // (re)attach the held node
  }

  /* =====================================================================
     WIRING (survives the theme's form re-renders)
     ===================================================================== */
  var observer = null, scheduled = false;
  function refresh() {
    if (observer) observer.disconnect();
    /* fillSwatchImages runs BEFORE applyGallery: colorPlan() needs every color to have
       an <img>, or it falls back to showing all colors. */
    try { updateLabels(); updateReviewCount(); fillSwatchImages(); applyGallery(); applyAvailability(); placeSizeGuide(); initAccordion(); } catch (e) {}
    if (observer) observer.observe(document.querySelector('product-info') || document.body, { childList: true, subtree: true });
  }
  function schedule() { if (scheduled) return; scheduled = true; setTimeout(function () { scheduled = false; refresh(); }, 120); }

  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.name || !/^options\[/i.test(el.name)) return;
    if (/colou?r/i.test(el.name)) lastColorVal = el.value; // remember the color the user picked
    refresh();
    setTimeout(refresh, 350); // re-assert after the theme finishes its async re-render
  });

  function start() {
    if (window.MutationObserver) observer = new MutationObserver(schedule);
    refresh();
    setTimeout(refresh, 800);   // Judge.me populates the rating badge asynchronously
    setTimeout(refresh, 2500);
    setTimeout(refresh, 5500);  // Kiwi size-chart injects late (its waitForElementDelay)
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
